import { PointsService } from './pointsService';
import { StepAction, JourneyStep } from '../types/journey';

/**
 * Service for managing points integration with the startup journey workflow
 * Handles action costs, rewards, and earning suggestions
 */
export class JourneyPointsService {

  /**
   * Check if user has sufficient points for an action
   */
  static async checkSufficientPoints(userId: string, pointsCost: number): Promise<{
    sufficient: boolean;
    currentPoints: number;
    shortfall?: number;
  }> {
    try {
      const { total: currentPoints } = await PointsService.getUserPoints(userId);
      
      return {
        sufficient: currentPoints >= pointsCost,
        currentPoints,
        shortfall: currentPoints < pointsCost ? pointsCost - currentPoints : undefined
      };
    } catch (error) {
      console.error('Error checking points balance:', error);
      return {
        sufficient: false,
        currentPoints: 0,
        shortfall: pointsCost
      };
    }
  }

  /**
   * Deduct points for performing a workflow action
   */
  static async deductActionCost(
    userId: string, 
    action: StepAction, 
    stepTitle: string
  ): Promise<{ success: boolean; error?: any }> {
    try {
      if (action.pointsCost <= 0) {
        return { success: true };
      }

      const reason = `Journey action: ${action.text} (${stepTitle})`;
      return await PointsService.deductPoints(userId, action.pointsCost, reason);
    } catch (error) {
      console.error('Error deducting action cost:', error);
      return { success: false, error };
    }
  }

  /**
   * Award points for completing a workflow action
   */
  static async awardActionReward(
    userId: string, 
    action: StepAction, 
    stepTitle: string
  ): Promise<{ success: boolean; error?: any }> {
    try {
      if (action.pointsReward <= 0) {
        return { success: true };
      }

      const reason = `Journey reward: ${action.text} completed (${stepTitle})`;
      return await PointsService.awardPoints(userId, action.pointsReward, reason);
    } catch (error) {
      console.error('Error awarding action reward:', error);
      return { success: false, error };
    }
  }

  /**
   * Award bonus points for step completion
   */
  static async awardStepCompletionBonus(
    userId: string, 
    step: JourneyStep, 
    hasReflection: boolean = false
  ): Promise<{ success: boolean; pointsAwarded: number; error?: any }> {
    try {
      let totalPoints = 0;

      // Base completion bonus (sum of all action rewards)
      const baseBonus = step.actions.reduce((total, action) => total + action.pointsReward, 0);
      if (baseBonus > 0) {
        const baseResult = await PointsService.awardPoints(
          userId, 
          baseBonus, 
          `Step completion bonus: ${step.title}`
        );
        if (baseResult.success) {
          totalPoints += baseBonus;
        }
      }

      // Reflection bonus
      if (hasReflection) {
        const reflectionBonus = 3;
        const reflectionResult = await PointsService.awardPoints(
          userId, 
          reflectionBonus, 
          `Reflection bonus: ${step.title}`
        );
        if (reflectionResult.success) {
          totalPoints += reflectionBonus;
        }
      }

      return { success: true, pointsAwarded: totalPoints };
    } catch (error) {
      console.error('Error awarding step completion bonus:', error);
      return { success: false, pointsAwarded: 0, error };
    }
  }

  /**
   * Award milestone bonus points for journey progress
   */
  static async awardMilestoneBonus(
    userId: string, 
    milestoneType: 'quarter' | 'half' | 'complete',
    journeyPersona: string
  ): Promise<{ success: boolean; pointsAwarded: number; error?: any }> {
    try {
      const bonusPoints = {
        quarter: 25,
        half: 50,
        complete: 100
      };

      const points = bonusPoints[milestoneType];
      const reason = `Journey milestone: ${milestoneType} completion (${journeyPersona})`;
      
      const result = await PointsService.awardPoints(userId, points, reason);
      
      return {
        success: result.success,
        pointsAwarded: result.success ? points : 0,
        error: result.error
      };
    } catch (error) {
      console.error('Error awarding milestone bonus:', error);
      return { success: false, pointsAwarded: 0, error };
    }
  }

  /**
   * Calculate net points for an action (reward - cost)
   */
  static calculateNetPoints(action: StepAction): number {
    return action.pointsReward - action.pointsCost;
  }

  /**
   * Calculate total points for a step (all actions + completion bonus)
   */
  static calculateStepTotalPoints(step: JourneyStep, includeReflectionBonus: boolean = true): {
    totalCost: number;
    totalReward: number;
    netPoints: number;
    completionBonus: number;
  } {
    const totalCost = step.actions.reduce((total, action) => total + action.pointsCost, 0);
    const totalReward = step.actions.reduce((total, action) => total + action.pointsReward, 0);
    const completionBonus = totalReward + (includeReflectionBonus ? 3 : 0);
    
    return {
      totalCost,
      totalReward,
      netPoints: totalReward - totalCost,
      completionBonus
    };
  }

  /**
   * Get earning suggestions when user has insufficient points
   */
  static getEarningSuggestions(shortfall: number): {
    suggestions: Array<{
      activity: string;
      points: number;
      description: string;
      timeEstimate: string;
    }>;
    quickestPath: string;
  } {
    const suggestions = [
      {
        activity: 'Complete surveys',
        points: 15,
        description: 'Answer other users\' market research surveys',
        timeEstimate: '5-10 minutes each'
      },
      {
        activity: 'Participate in tasks',
        points: 12,
        description: 'Help other entrepreneurs with their projects',
        timeEstimate: '15-30 minutes each'
      },
      {
        activity: 'Daily login bonus',
        points: 10,
        description: 'Log in daily to earn streak bonuses',
        timeEstimate: '1 minute'
      },
      {
        activity: 'Join team applications',
        points: 8,
        description: 'Apply to join startup teams',
        timeEstimate: '10-15 minutes each'
      },
      {
        activity: 'Provide feedback',
        points: 5,
        description: 'Give feedback on pre-release projects',
        timeEstimate: '5 minutes each'
      }
    ];

    // Find quickest path to earn required points
    let remainingShortfall = shortfall;
    const quickestActivities: string[] = [];
    
    for (const suggestion of suggestions) {
      if (remainingShortfall <= 0) break;
      
      const timesNeeded = Math.ceil(remainingShortfall / suggestion.points);
      quickestActivities.push(`${timesNeeded}x ${suggestion.activity}`);
      remainingShortfall -= timesNeeded * suggestion.points;
    }

    return {
      suggestions,
      quickestPath: quickestActivities.join(', ')
    };
  }

  /**
   * Log points transaction for workflow activities
   */
  static async logWorkflowTransaction(
    userId: string,
    points: number,
    transactionType: 'deduction' | 'reward' | 'bonus',
    activity: string,
    stepId?: string
  ): Promise<{ success: boolean; error?: any }> {
    try {
      // Use the existing points service which handles transaction logging
      if (transactionType === 'deduction') {
        return await PointsService.deductPoints(userId, Math.abs(points), `Workflow: ${activity}`);
      } else {
        return await PointsService.awardPoints(userId, Math.abs(points), `Workflow: ${activity}`);
      }
    } catch (error) {
      console.error('Error logging workflow transaction:', error);
      return { success: false, error };
    }
  }

  /**
   * Get points summary for a journey
   */
  static calculateJourneyPointsSummary(steps: JourneyStep[]): {
    totalPotentialCost: number;
    totalPotentialReward: number;
    totalNetPoints: number;
    completedStepsPoints: number;
    remainingStepsPoints: number;
  } {
    let totalPotentialCost = 0;
    let totalPotentialReward = 0;
    let completedStepsPoints = 0;
    let remainingStepsPoints = 0;

    steps.forEach(step => {
      const stepPoints = this.calculateStepTotalPoints(step);
      totalPotentialCost += stepPoints.totalCost;
      totalPotentialReward += stepPoints.totalReward;

      if (step.status === 'completed') {
        completedStepsPoints += stepPoints.netPoints;
      } else if (step.status === 'current' || step.status === 'locked') {
        remainingStepsPoints += stepPoints.netPoints;
      }
    });

    return {
      totalPotentialCost,
      totalPotentialReward,
      totalNetPoints: totalPotentialReward - totalPotentialCost,
      completedStepsPoints,
      remainingStepsPoints
    };
  }

  /**
   * Check if user qualifies for milestone bonus
   */
  static checkMilestoneEligibility(steps: JourneyStep[]): {
    quarterMilestone: boolean;
    halfMilestone: boolean;
    completeMilestone: boolean;
    completedSteps: number;
    totalSteps: number;
  } {
    const totalSteps = steps.length;
    const completedSteps = steps.filter(step => step.status === 'completed').length;
    const completionRate = completedSteps / totalSteps;

    return {
      quarterMilestone: completionRate >= 0.25 && completedSteps >= 1,
      halfMilestone: completionRate >= 0.5 && completedSteps >= 2,
      completeMilestone: completionRate >= 1.0 && completedSteps === totalSteps,
      completedSteps,
      totalSteps
    };
  }
}